import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CircleDot,
  Shield,
  Microscope,
  Syringe,
  Scan,
  Scissors,
  HeartPulse,
} from "lucide-react";

const specialities = [
  {
    title: "Ovarian Cancer Treatment",
    icon: CircleDot,
    description:
      "Comprehensive diagnosis and surgical treatment for ovarian cancer with advanced techniques.",
    span: "md:col-span-4",
    link: "/services/ovarian-cancer-treatment",
  },
  {
    title: "Cervical Cancer Treatment",
    icon: Shield,
    description:
      "Expert cervical cancer care including screening, diagnosis, and surgical management.",
    span: "md:col-span-4",
    link: "/services/cervical-cancer-treatment",
  },
  {
    title: "Uterine (Endometrial) Cancer",
    icon: HeartPulse,
    description:
      "Specialized treatment plans for uterine and endometrial cancers with personalized care.",
    span: "md:col-span-4",
    link: "/services/uterine-cancer-treatment",
  },
  {
    title: "Vaginal & Vulval Cancer Care",
    icon: Microscope,
    description:
      "Expert management of vaginal and vulval cancers with minimally invasive approaches.",
    span: "md:col-span-4 lg:col-span-6",
    link: "/services/vaginal-vulval-cancer-care",
  },
  {
    title: "HIPEC Surgery",
    icon: Syringe,
    description:
      "Hyperthermic Intraperitoneal Chemotherapy — an advanced treatment for selected cancer cases in Kolkata.",
    span: "md:col-span-4 lg:col-span-6",
    link: "/services/hipec-surgery",
  },
  {
    title: "Laparoscopic & Robotic Surgery",
    icon: Scissors,
    description:
      "Minimally invasive surgical techniques for faster recovery and better outcomes.",
    span: "md:col-span-4 lg:col-span-6",
    link: "/services/laparoscopic-robotic-surgery",
  },
  {
    title: "Cancer Screening & Preventive Oncology",
    icon: Scan,
    description:
      "Early detection programs and preventive oncology services for gynecologic cancers.",
    span: "md:col-span-4 lg:col-span-6",
    link: "/services/cancer-screening",
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
};

export default function SpecialitiesSection() {
  return (
    <section
      id="specialities"
      data-testid="specialities-section"
      className="py-24 md:py-32 bg-[#F2EFE9]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#8FA998] mb-4">
            Our Specialities
          </p>

          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#1E3F33] leading-tight max-w-2xl">
            Comprehensive Gynecologic Cancer Treatment
          </h2>

          <p className="text-base sm:text-lg text-[#4A6359] mt-4 max-w-2xl leading-relaxed">
            We provide specialized diagnosis and treatment for all major
            gynecologic cancers using advanced surgical and non-surgical
            techniques.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-12 gap-5"
        >
          {specialities.map((item, i) => (
            <motion.div
              key={item.title}
              variants={cardVariants}
              className={`${item.span} bg-[#F9F9F6] rounded-2xl md:rounded-[2rem] p-7 md:p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#1E3F33]/5 transition-all duration-300`}
            >
              <div className="w-12 h-12 rounded-full bg-[#1E3F33]/10 flex items-center justify-center mb-5">
                <item.icon className="w-6 h-6 text-[#1E3F33]" />
              </div>

              <h3 className="font-serif text-xl sm:text-2xl text-[#1E3F33] mb-3 leading-snug">
                {item.title}
              </h3>

              <p className="text-sm sm:text-base text-[#4A6359] leading-relaxed mb-4">
                {item.description}
              </p>

              <Link
                to={item.link}
                className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3F33]"
              >
                Learn more <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}